import React, { useState, useEffect } from 'react';
import { Select, Card, Space, Button, Dropdown, Drawer, message, Spin } from 'antd';
import { WechatOutlined, DownloadOutlined, DownOutlined, LinkOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { SidebarCaseInfo } from '../../../services/wechatSidebar';
import { updateCase } from '../../../services/case';
import { getAllLeads, Lead, LeadStatus } from '../../../services/lead';

interface CaseSelectorProps {
  cases: SidebarCaseInfo[];
  selectedCaseId: number | null;
  onChange: (caseId: number) => void;
  loading?: boolean;
  caseData?: any; // 完整的案件数据
  onRefresh?: () => void;
}

const CaseSelector: React.FC<CaseSelectorProps> = ({
  cases,
  selectedCaseId,
  onChange,
  loading = false,
  caseData,
  onRefresh
}) => {
  const [linkLeadModalVisible, setLinkLeadModalVisible] = useState(false);
  const [leadId, setLeadId] = useState<number | null>(null);
  const [linking, setLinking] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const selectedCase = cases.find(c => c.id === selectedCaseId);

  // 手机号脱敏函数
  const maskPhone = (phone: string): string => {
    if (!phone || phone.length < 7) return phone;
    return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
  };

  // 加载线索列表
  useEffect(() => {
    if (linkLeadModalVisible) {
      loadLeads();
    }
  }, [linkLeadModalVisible]);

  const loadLeads = async (keyword?: string) => {
    setLeadsLoading(true);
    try {
      const response = await getAllLeads({
        page: 1,
        pageSize: 100,
        search: keyword, // 支持后端搜索
      });
      setLeads(response.items);
    } catch (error) {
      console.error('加载线索列表失败:', error);
      message.error('加载线索列表失败');
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    if (value) {
      loadLeads(value);
    } else {
      loadLeads();
    }
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      active: '进行中',
      closed: '已结案',
      suspended: '暂停',
      completed: '已完成',
      cancelled: '已取消',
    };
    return texts[status] || status;
  };

  // 构建案件切换菜单
  const caseMenuItems: MenuProps['items'] = cases.map(c => ({
    key: c.id,
    label: (
      <div>
        <div style={{ fontWeight: 500 }}>{c.case_name || '未命名案件'}</div>
        <div style={{ fontSize: 12, color: '#999' }}>
          {c.case_number} · {getStatusText(c.case_status)}
        </div>
      </div>
    ),
    onClick: () => onChange(c.id)
  }));

  const handleDownload = () => {
    // TODO: 实现打包下载功能
    message.info('打包下载功能开发中');
  };

  const handleLinkLead = async () => {
    if (!leadId || !selectedCaseId) {
      message.warning('请选择要关联的线索');
      return;
    }

    setLinking(true);
    try {
      // 通过更新案件的 lead_id 来关联线索
      await updateCase(selectedCaseId, { lead_id: leadId });
      message.success('关联线索成功');
      setLinkLeadModalVisible(false);
      setLeadId(null);
      onRefresh?.();
    } catch (error: any) {
      console.error('关联线索失败:', error);
      message.error('关联线索失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLinking(false);
    }
  };

  if (!selectedCase) {
    return (
      <div className="case-selector">
        <Select
          value={selectedCaseId}
          onChange={onChange}
          style={{ width: '100%' }}
          placeholder="选择案件"
          loading={loading}
        >
          {cases.map(c => (
            <Select.Option key={c.id} value={c.id}>
              {c.case_name || '未命名案件'}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  }

  return (
    <div className="case-selector">
      <Card className="case-info-card" bordered={false}>
        {/* 案件标题行 */}
        <div className="case-title-row">
          <span className="case-title">{selectedCase.case_name || '未命名案件'}</span>
          {cases.length > 1 && (
            <Dropdown menu={{ items: caseMenuItems }} trigger={['click']}>
              <Button type="text" size="small" icon={<DownOutlined />} />
            </Dropdown>
          )}
        </div>

        {/* 案件信息列表 */}
        <div className="case-info-list">
          {/* 负责人 */}
          <div className="case-info-item">
            <span className="info-label">负责人：</span>
            <span className="info-value info-link">
              {selectedCase.responsible_lawyer_name || '-'}
            </span>
          </div>

          {/* 案件类型 */}
          <div className="case-info-item">
            <span className="info-label">案件类型：</span>
            <span className="info-value">{caseData?.case_type || '-'}</span>
          </div>

          {/* 审级 - 从 details.case_level 获取 */}
          <div className="case-info-item">
            <span className="info-label">审级：</span>
            <span className="info-value">
              {(caseData as any)?.details?.case_level || '-'}
            </span>
          </div>

          {/* 助理律师 - 从 assistant_lawyer 关系获取 */}
          <div className="case-info-item">
            <span className="info-label">助理律师：</span>
            <span className="info-value">
              {(caseData as any)?.assistant_lawyer?.real_name || '-'}
            </span>
          </div>

          {/* 案件承接时间 */}
          <div className="case-info-item">
            <span className="info-label">案件承接时间：</span>
            <span className="info-value">{selectedCase.accept_date || '-'}</span>
          </div>

          {/* 关联线索 */}
          <div className="case-info-item">
            <span className="info-label">关联线索：</span>
            <span className="info-value">
              {caseData?.lead_id ? (
                <>
                  {(caseData as any)?.lead?.contact_name || '-'}
                  {(caseData as any)?.lead?.phone && ` / ${maskPhone((caseData as any).lead.phone)}`}
                </>
              ) : (
                <Button 
                  type="link" 
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => setLinkLeadModalVisible(true)}
                  style={{ 
                    padding: 0,
                    height: 'auto',
                    fontSize: 13,
                    color: '#1890ff'
                  }}
                >
                  立即绑定线索
                </Button>
              )}
            </span>
          </div>
        </div>
      </Card>

      {/* 绑定线索弹窗 */}
      <Drawer
        title="选择关联线索"
        placement="bottom"
        open={linkLeadModalVisible}
        onClose={() => {
          setLinkLeadModalVisible(false);
          setLeadId(null);
        }}
        height="50vh"
        className="link-lead-drawer"
        footer={
          <div style={{ 
            display: 'flex', 
            gap: 12,
            padding: '12px 0'
          }}>
            <Button 
              size="large"
              style={{ flex: 1, height: 44, borderRadius: 8 }}
              onClick={() => {
                setLinkLeadModalVisible(false);
                setLeadId(null);
              }}
            >
              取消
            </Button>
            <Button 
              type="primary"
              size="large"
              style={{ flex: 1, height: 44, borderRadius: 8 }}
              loading={linking}
              onClick={handleLinkLead}
            >
              确定
            </Button>
          </div>
        }
      >
        <div style={{ padding: '8px 0' }}>
          {/* 搜索框 */}
          <div style={{ marginBottom: 16 }}>
            <Select
              value={leadId}
              onChange={setLeadId}
              style={{ width: '100%' }}
              placeholder="请输入线索名称/线索手机号"
              showSearch
              size="large"
              suffixIcon={<span style={{ color: '#999' }}>🔍</span>}
              filterOption={false} // 禁用前端过滤，使用后端搜索
              onSearch={handleSearch} // 调用后端搜索
              notFoundContent={leadsLoading ? <Spin size="small" /> : '暂无可用线索'}
              loading={leadsLoading}
            >
              {leads.map(lead => (
                <Select.Option 
                  key={lead.id} 
                  value={lead.id}
                >
                  <div style={{ 
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div style={{ 
                      fontSize: 15,
                      color: '#333',
                      marginBottom: 4
                    }}>
                      {lead.contact_name} / {maskPhone(lead.phone || '')}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CaseSelector;
